import React from "react";
import type { AuditChange, EmptyFlag, SourceDiff } from "../model/auditUtils";

// ─── Badge ────────────────────────────────────────────────────────────────────

const KIND_META: Record<AuditChange["kind"], { label: string; cls: string }> = {
  became_empty: {
    label: "Became empty",
    cls: "bg-red-50 text-red-700 border-red-200",
  },
  became_filled: {
    label: "Became filled",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  numeric_jump: {
    label: "Numeric jump",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
  },
  changed: {
    label: "Changed",
    cls: "bg-blue-50 text-blue-700 border-blue-200",
  },
};

function Badge({ kind }: { kind: AuditChange["kind"] }) {
  const m = KIND_META[kind];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

function fmtVal(v: unknown) {
  if (v === null || v === undefined || v === "") {
    return <span className="text-gray-300 italic">empty</span>;
  }
  return <span className="text-gray-800 font-medium">{String(v)}</span>;
}

function fmtDelta(d?: number) {
  if (d === undefined) return null;
  const sign = d >= 0 ? "+" : "";
  const tone =
    d < 0 ? "text-red-700" : d > 0 ? "text-emerald-700" : "text-amber-700";
  return (
    <span className={`text-[10px] font-semibold tabular-nums ${tone}`}>
      {sign}
      {(d * 100).toFixed(1)}%
    </span>
  );
}

// ─── List of AuditChanges ─────────────────────────────────────────────────────

export function ChangeList({
  changes,
  groupTitle,
}: {
  changes: AuditChange[];
  groupTitle?: string;
}) {
  if (changes.length === 0) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {groupTitle && (
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
            {groupTitle}
          </span>
          <span className="ml-2 text-[11px] text-gray-400">
            {changes.length} change{changes.length > 1 ? "s" : ""}
          </span>
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {changes.map((c) => (
          <li
            key={`${c.kind}:${c.key}:${c.before}:${c.after}`}
            className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50/60 transition-colors"
          >
            <div className="shrink-0 w-28">
              <Badge kind={c.kind} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-700 truncate">
                {c.label}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                {fmtVal(c.before)}
                <span className="text-gray-300">→</span>
                {fmtVal(c.after)}
                {c.deltaPct !== undefined && (
                  <span className="ml-2">{fmtDelta(c.deltaPct)}</span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Empty-flag list ──────────────────────────────────────────────────────────

export function EmptyFlagList({
  flags,
  groupTitle,
}: {
  flags: EmptyFlag[];
  groupTitle?: string;
}) {
  if (flags.length === 0) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-white overflow-hidden">
      {groupTitle && (
        <div className="border-b border-red-100 bg-red-50 px-4 py-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-red-700">
            {groupTitle}
          </span>
          <span className="ml-2 text-[11px] text-red-500">
            {flags.length} missing value{flags.length > 1 ? "s" : ""}
          </span>
        </div>
      )}
      <ul className="divide-y divide-red-50">
        {flags.map((f) => (
          <li
            key={`${f.key}:${f.label}`}
            className="px-4 py-2 flex items-center gap-3 text-xs"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
            <span className="font-semibold text-gray-700">{f.label}</span>
            <span className="ml-auto text-gray-400 italic">missing</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Cross-source diff list ───────────────────────────────────────────────────

export function SourceDiffList({
  diffs,
  groupTitle,
}: {
  diffs: SourceDiff[];
  groupTitle?: string;
}) {
  if (diffs.length === 0) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {groupTitle && (
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
            {groupTitle}
          </span>
          <span className="ml-2 text-[11px] text-gray-400">
            {diffs.length} discrepanc{diffs.length > 1 ? "ies" : "y"}
          </span>
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {diffs.map((d) => (
          <li
            key={`${d.key}:${d.label}`}
            className="px-4 py-2.5 flex items-start gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-700">
                {d.label}
              </div>
              <div className="mt-1 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
                {d.values.map((v) => (
                  <React.Fragment key={`${d.key}:${v.docLabel}:${v.value}`}>
                    <span
                      className="text-[10px] uppercase tracking-wide text-gray-400 truncate max-w-[240px]"
                      title={v.docLabel}
                    >
                      {v.docLabel}
                    </span>
                    <span className="text-xs tabular-nums text-gray-800 font-medium">
                      {v.value}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

export function AuditEmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center">
      <svg
        className="mx-auto h-8 w-8 text-gray-300"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
        />
      </svg>
      <p className="mt-3 text-sm text-gray-500">{message}</p>
    </div>
  );
}
